import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';

interface Fournisseur {
  id: number;
  raisonSociale: string;
}

interface Emplacement {
  id: number;
  nom: string;
  code?: string;
}

interface Produit {
  id: number;
  name: string;
  salePrice: number;
  stockMax: number;
  fournisseur_principal_id: number;
}

interface OrderLine {
  id: string;
  produit_id: number;
  produit: string;
  quantite: number;
  prixUnitaire: number;
  montantTotal: number;
  stockMax: number;
}
export default function PurchaseOrderForm({ 
  order, 
  onClose, 
  initialProduct // ← Nouveau prop
}: { 
  order?: any; 
  onClose: () => void; 
  initialProduct?: { 
    id: number; 
    name: string; 
    suggestedQuantity?: number; 
    fournisseurId?: number 
    prixUnitaire?: number, 
    stockMax?: number // ou tu peux récupérer le vrai max plus tard
  } 
}) {
  const [formData, setFormData] = useState({
    fournisseur: '',
    devise: 'GNF',
    // dateCommande: new Date().toISOString().split('T')[0],
    // dateLivraisonPrevue: '',
    notes: '',
    conditionsPaiement: '30 jours Net'
  });

  const [deletedLines, setDeletedLines] = useState<number[]>([]);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([]);
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine] = useState({
    produit: '',
    quantite: 1,
    prixUnitaire: 0,  // <-- prix par défaut
    stockMax: 0
  });

  // Données dynamiques
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [emplacements, setEmplacements] = useState<Emplacement[]>([]);
  const [produits, setProduits] = useState<Produit[]>([]);
  const [produitsFournisseur, setProduitsFournisseur] = useState<Produit[]>([]);

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resFourn, resProd] = await Promise.all([
          getResources('fournisseurs'),
          getResources('produits?with=stockMax,salePrice,fournisseur_principal_id'),
        ]);
        setFournisseurs(resFourn.data.data || resFourn.data);
        setProduits(resProd.data.data || resProd.data);
      } catch (err) {
        console.error('Erreur chargement', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!initialProduct) return;

    // 1. Pré-remplir le fournisseur
    if (initialProduct.fournisseurId) {
      setFormData(prev => ({
        ...prev,
        fournisseur: initialProduct.fournisseurId.toString()
      }));
    }

    // 2. Ajouter la ligne avec les bonnes valeurs
    const qty = initialProduct.suggestedQuantity || 50;
    const prix = initialProduct.prixUnitaire || 0;
    const stockMax = initialProduct.stockMax || 999999;

    const line: OrderLine = {
      id: Date.now().toString(),
      produit_id: initialProduct.id,
      produit: initialProduct.name,
      quantite: qty,
      prixUnitaire: prix,
      montantTotal: qty * prix,
      stockMax: stockMax
    };

    setOrderLines([line]);

    // Toast informatif
    toast.success(`"${initialProduct.name}" ajouté avec ${qty} unités suggérées`);
  }, [initialProduct]);
  // Filtrer produits du fournisseur
  useEffect(() => {
    if (formData.fournisseur) {
      const fournisseurId = parseInt(formData.fournisseur);
      const filtered = produits.filter(p => p.fournisseur_principal_id === fournisseurId);
      setProduitsFournisseur(filtered);
    } else {
      setProduitsFournisseur([]);
    }
  }, [formData.fournisseur, produits]);

  const handleProduitChange = (produitNom: string) => {
    const produit = produitsFournisseur.find(p => p.name === produitNom);
    if (produit) {
      setNewLine({
        produit: produitNom,
        prixUnitaire: produit.salePrice,
        stockMax: produit.stockMax,
        quantite: 1
      });
    }
  };

  // Préchargement des données si order existe (mode EDIT)
  useEffect(() => {
    if (order) {
      console.log('Chargement de la commande pour édition', order);
      // Pré-remplir les infos générales
      setFormData({
        fournisseur: order.fournisseur_id || '',
        devise: order.devise || 'GNF',
        notes: order.motif_annulation || '',
        conditionsPaiement: order.conditionsPaiement || '30 jours Net'
      });

      // Charger les lignes existantes
      const lignes = order.lignes?.map((l: any) => ({
        id: l.id.toString(),
        produit_id: l.produit_id,
        produit: l.produit,
        quantite: l.quantite,
        prixUnitaire: l.prix_unitaire_ht,
        montantTotal: l.quantite * l.prix_unitaire_ht,
        stockMax: l.stockMax || 999999
      })) || [];

      setOrderLines(lignes);
    }
  }, [order]);

  const handleAddLine = () => {
    if (!newLine.produit) {
      alert('Veuillez sélectionner un produit');
      return;
    }

    // Trouver le produit correspondant
    const produit = produitsFournisseur.find(p => p.name === newLine.produit);
    if (!produit) return;

    // Calculer la quantité totale actuelle du même produit
    const totalQuantiteExistante = orderLines
      .filter(l => l.produit_id === produit.id)
      .reduce((sum, l) => sum + l.quantite, 0);

    const quantiteTotale = totalQuantiteExistante + newLine.quantite;

    if (quantiteTotale > produit.stockMax) {
      alert(`Quantité totale dépassée pour ce produit. Maximum autorisé : ${produit.stockMax}`);
      return;
    }

    // Créer la ligne
    const line: OrderLine = {
      id: Date.now().toString(),
      produit_id: produit.id,
      produit: produit.name,
      quantite: newLine.quantite,
      prixUnitaire: newLine.prixUnitaire, // <-- prix modifiable
      montantTotal: newLine.quantite * newLine.prixUnitaire,
      stockMax: produit.stockMax
    };

    setOrderLines([...orderLines, line]);
    setNewLine({ produit: '', quantite: 1, prixUnitaire: 0, stockMax: 0 });
    setShowAddLine(false);
  };

  const handleRemoveLine = (id: string) => {
    const idNum = Number(id);

    // Si la ligne existe en base, on l'ajoute à la liste à supprimer
    if (order?.lignes?.some(l => Number(l.id) === idNum)) {
      setDeletedLines(prev => [...prev, idNum]);
    }

    // On retire la ligne de l'affichage
    setOrderLines(orderLines.filter(l => l.id !== id));
  };
  console.log("deletedLines après suppression :", deletedLines);

  const handleUpdateQuantite = (id: string, value: number) => {
    const qty = Math.max(1, value);
    setOrderLines(prev =>
      prev.map(line => {
        if (line.id === id) {
          if (qty > line.stockMax) {
            alert(`Maximum autorisé : ${line.stockMax}`);
            return line;
          }
          return { ...line, quantite: qty, montantTotal: qty * line.prixUnitaire };
        }
        return line;
      })
    );
  };

  const calculateTotal = () => orderLines.reduce((s, l) => s + l.montantTotal, 0);
  const formatAmount = (n: number) => new Intl.NumberFormat('fr-FR').format(n);


  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!formData.fournisseur) return alert('Sélectionnez un fournisseur');
  if (orderLines.length === 0) return alert('Ajoutez au moins une ligne');

  const payload = {
  fournisseur_id: parseInt(formData.fournisseur),
  devise: formData.devise,
  conditions_paiement: formData.conditionsPaiement,
  notes: formData.notes,
  statut: order ? order.statut : 'BROUILLON',

  lignes: orderLines.map(l => {
    const idNum = Number(l.id);
    const isExisting = order?.lignes?.some(ol => ol.id.toString() === l.id);

    return {
      id: isExisting ? idNum : null,
      produit_id: Number(l.produit_id),
      quantite_commandee: l.quantite,
      prix_unitaire_ht: l.prixUnitaire,
    };
  }),

  _delete_lignes: deletedLines
};


  console.log('contenu a verifie:', payload);
  try {
    if (order) {
      // --- MODE EDIT ---
      await updateResource(`bons-commande`,order.id, payload);
      toast.success("Bon de commande modifié avec succès !");
    } else {
      // --- MODE CREATE ---
      await createResource('bons-commande', payload);
      toast.success("Bon de commande créé en BROUILLON !");
    }

    onClose();
  } catch (err) {
    console.error(err);
    toast.error("Erreur lors de l’enregistrement du bon de commande");
  }
};


  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {order ? 'Modifier le Bon de Commande' : 'Nouveau Bon de Commande'}
          </h2>
          <p className="text-sm text-gray-600">
            {order ? `BC N° ${order.numero}` : 'Créer un nouveau bon de commande fournisseur'}
          </p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      <form className="p-6 space-y-6" onSubmit={e => e.preventDefault()}>
        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fournisseur <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.fournisseur}
              onChange={e => {
                setFormData({ ...formData, fournisseur: e.target.value });
                setOrderLines([]);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Sélectionner un fournisseur</option>
              {fournisseurs.map(f => (
                <option key={f.id} value={f.id}>{f.raisonSociale}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
            <select value={formData.devise} onChange={e => setFormData({ ...formData, devise: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="GNF">Franc Guinéen (GNF)</option>
              <option value="USD">Dollar US (USD)</option>
              <option value="EUR">Euro (EUR)</option>
            </select>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de Commande <span className="text-red-500">*</span></label>
            <input type="date" value={formData.dateCommande} onChange={e => setFormData({ ...formData, dateCommande: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de Livraison Prévue</label>
            <input type="date" value={formData.dateLivraisonPrevue} onChange={e => setFormData({ ...formData, dateLivraisonPrevue: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div> */}
        </div>

        {/* Notes + Lignes + Boutons (identique) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes / Remarques</label>
          <textarea
            value={formData.notes}
            onChange={e => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
            placeholder="Informations complémentaires..."
          />
        </div>

        {/* === Lignes de commande (inchangées, juste raccourcies pour lisibilité) === */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Lignes de Commande ({orderLines.length})</h3>
            <button
              type="button"
              onClick={() => setShowAddLine(true)}
              disabled={!formData.fournisseur}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              Ajouter une ligne
            </button>
          </div>

          {showAddLine && (
            <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produit</label>
                  <select
                    value={newLine.produit}
                    onChange={e => handleProduitChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Choisir un produit</option>
                    {produitsFournisseur.map(p => (
                      <option key={p.id} value={p.name}>
                        {p.name} (Max: {p.stockMax})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantité (max {newLine.stockMax})</label>
                  <input
                    type="number"
                    min="1"
                    max={newLine.stockMax}
                    value={newLine.quantite}
                    onChange={e => {
                      const v = parseInt(e.target.value) || 1;
                      if (v > newLine.stockMax) {
                        alert(`Maximum: ${newLine.stockMax}`);
                        return;
                      }
                      setNewLine({ ...newLine, quantite: v });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix Unitaire</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newLine.prixUnitaire}
                    onChange={e => setNewLine({...newLine, prixUnitaire: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => { setShowAddLine(false); setNewLine({ produit: '', quantite: 1, prixUnitaire: 0, stockMax: 0 }); }} className="px-4 py-2 border rounded-lg text-sm">Annuler</button>
                <button type="button" onClick={handleAddLine} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Ajouter</button>
              </div>
            </div>
          )}

          {/* Tableau des lignes (identique à ta version précédente) */}
          {orderLines.length > 0 ? (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix U.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orderLines.map(l => (
                    <tr key={l.id}>
                      <td className="px-4 py-3 text-sm">{l.produit}</td>
                      <td className="px-4 py-3">
                        <input type="number" min="1" max={l.stockMax} value={l.quantite} onChange={e => handleUpdateQuantite(l.id, parseInt(e.target.value) || 1)} className="w-20 px-2 py-1 border rounded text-sm" />
                        <span className="text-xs text-gray-500 ml-1">/ {l.stockMax}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatAmount(l.prixUnitaire)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{formatAmount(l.montantTotal)} {formData.devise}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => handleRemoveLine(l.id)} className="text-red-600 hover:text-red-900 text-sm">Supprimer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-medium">
                  <tr>
                    <td colSpan={3} className="px-4 py-3 text-right">Total HT :</td>
                    <td colSpan={2} className="px-4 py-3 text-lg font-bold">{formatAmount(calculateTotal())} {formData.devise}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg text-gray-500">
              Aucune ligne ajoutée
            </div>
          )}
        </div>

        {/* Boutons finaux */}
        <div className="flex justify-end gap-4 pt-6 border-t">
          <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg">Annuler</button>
          <button type="button" onClick={handleSubmit} className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800">
            Enregistrer BROUILLON
          </button>
        </div>

      </form>
    </div>
  );
}